import React, { lazy, Suspense, useEffect } from 'react';
import styled from 'styled-components';

import SubscribeMobileMountains from 'src/components/SubscribeMobileMountains';
import useSigmaFeature from 'src/utils/featureToggleSigma/useSigmaFeature';
import { useShowSubscribe } from 'src/components/SubscribeMobileMountains/hooks/useShowSubscribe';
import { useCountry } from '@lt/localization';
import { isShow as isShowMobileAppPopup } from 'src/components/Banners/MobileAppPopup';
import { scrollToElement } from 'src/utils/domUtils';
import { HotelCounter } from 'src/components/HotelCounter';
import type { SearchType } from 'src/types/search';
import HintFactory from '../../../../../components/Hint/HintFactory';
import Title from '../../Title';
import Sorting from '../../Sorting';
import Overlay from '../../../containers/Overlay';
import Hotels from '../../Hotels';
import LazyPriceChart from '../../PriceChart/LazyPriceChart';
import LazySubscribe from '../../../containers/Subscribe/LazySubscribe';
import LazyFilters from '../../Filters/components/FiltersPanel';
import YandexMapsSearch from '../../YandexMapsSearch';

const EmptySearch = lazy(
  () => import(/* webpackChunkName: "EmptySearch" */ '../../EmptySearch'),
);
const EmptyFiltration = lazy(
  () =>
    import(/* webpackChunkName: "EmptyFiltration" */ '../../EmptyFiltration'),
);

const Map = lazy(
  () => import(/* webpackChunkName: "Map" */ '../../MapContainer'),
);

const FloatingPanelMobile = lazy(
  () =>
    import(
      /* webpackChunkName: "FloatingPanelMobile" */ '../../FloatingPanelMobile'
    ),
);

type SearchPageMobileProps = {
  hasSearchResults?: boolean;
  mapView: boolean;
  hideFilters: boolean;
  awaitFiltration: boolean;
  isFirstResultsFetched: boolean;
  isSubscribeShown: boolean;
  isEmptySearch: boolean;
  isEmptyFiltration: boolean;
  isHotelOnly: boolean;
  countryName?: string;
  isWhitelabel?: boolean;
  searchType: SearchType;
  hotelsCount: number;
};

const SearchPageMobile = ({
  hideFilters,
  mapView,
  awaitFiltration,
  isFirstResultsFetched,
  hasSearchResults = false,
  isSubscribeShown,
  isEmptySearch,
  isEmptyFiltration,
  isHotelOnly,
  countryName,
  isWhitelabel,
  searchType,
  hotelsCount,
}: SearchPageMobileProps): JSX.Element => {
  useEffect(() => {
    scrollToElement(document.body);
  }, [hideFilters]);

  const showResults =
    !mapView &&
    hideFilters &&
    !isSubscribeShown &&
    !isEmptySearch &&
    !isEmptyFiltration;
  const showSorting = showResults && hasSearchResults;
  const showLoadingOverlay = showResults && awaitFiltration;
  const showFloatingPanel =
    hideFilters &&
    ((isFirstResultsFetched && !isEmptySearch && !isSubscribeShown) ||
      isEmptyFiltration);

  const showPriceChart =
    !mapView && hideFilters && !isSubscribeShown && !isHotelOnly;
  const showMountains = !isWhitelabel && !isHotelOnly && showResults;

  const isEmptyResult = isEmptySearch || isEmptyFiltration;
  const { isRussia } = useCountry();
  const { isShow, handleCloseClick } = useShowSubscribe(
    !isShowMobileAppPopup(),
  );

  const [showYMaps] = useSigmaFeature('yandex_maps', 'on');

  const mapComponent = showYMaps ? (
    <YandexMapsSearch />
  ) : (
    <Suspense fallback={null}>
      <Map />
    </Suspense>
  );

  return (
    <div className="search-page-mobile">
      <HintFactory events isMobile attribute="data-rh" />

      {showResults && <StyledTitle />}
      {showPriceChart && isRussia && !isEmptyResult && <PriceChart />}
      {!hideFilters && (
        <LazyFilters show={!hideFilters} isHotelOnly={isHotelOnly} isMobile />
      )}
      {isSubscribeShown && !isHotelOnly && isRussia ? <LazySubscribe /> : null}
      {showSorting ? (
        <SortingContainer>
          <Sorting />
          <HotelCounter hotelCounter={hotelsCount} searchType={searchType} />
        </SortingContainer>
      ) : null}
      {showFloatingPanel ? (
        <Suspense fallback={null}>
          <SubscribeMobileMountains
            countryName={countryName}
            isMountainsActive={isShow && showMountains && isRussia}
            onCloseMountains={handleCloseClick}
          >
            <FloatingPanelMobile
              isMountainsActive={isShow && showMountains && isRussia}
              onCloseMountains={handleCloseClick}
            />
          </SubscribeMobileMountains>
        </Suspense>
      ) : null}

      <div className="search-page__content mobile">
        <div className="search-page__center">
          {showLoadingOverlay ? <Overlay /> : null}
          {mapView ? mapComponent : null}
          {showResults ? <Hotels /> : null}

          <Suspense fallback={null}>
            {isEmptySearch && <EmptySearch />}
            {isEmptyFiltration && <EmptyFiltration />}
          </Suspense>
        </div>
      </div>
    </div>
  );
};

const SortingContainer = styled.div`
  padding: 0 12px 8px 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  margin: 0 10px 5px;
  padding-top: 10px;
`;

const PriceChart = styled(LazyPriceChart)`
  margin-bottom: 16px;
`;

export default SearchPageMobile;
